import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Table from "../../../core/common/dataTable/index";
import { CycleTable } from "../../../core/data/interface"; //Les types des colones de notre tableau déclaré au préalable
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { AxiosError } from "axios";
import {
  Action,
  buildPermission,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import withPermission from "../../../core/common/autorisations/withPermission";
import { deleteCycle, getAllCycle } from "../../../api/settings";
import EditCycleModal from "./editCycle";
import CreateCycleModal from "./createCycle";

// bouton protégée pour créer une année universitaire
const ProtectedBtnAddCycle = withPermission(
  (props: any) => (
    <div className="mb-2">
      <button
        onClick={() => {
          props.setShowCreate(true);
        }}
        //   to="#"
        className="btn btn-primary d-flex align-items-center"
      >
        <i className="ti ti-square-rounded-plus me-2" />
        Nouveau Cycle
      </button>
    </div>
  ),
  [buildPermission(Resource.ACADEMIC_YEAR, Action.CREATE)],
  "masquer",
);

// bouton protégée pour modifier une année universitaire
const ProtectedBtnUpdateCycle = withPermission(
  (props: any) => (
    <li>
      <Link
        className="dropdown-item rounded-1"
        to="#"
        onClick={() => {
          props.setEditCycle(props.record.id);
          props.setShowEdit(true);
        }}
      >
        <i className="ti ti-edit-circle me-2" />
        Modifier
      </Link>
    </li>
  ),
  [buildPermission(Resource.ACADEMIC_YEAR, Action.UPDATE)],
  "masquer",
);

// bouton protégée pour supprimer une année universitaire
const ProtectedBtnDeleteCycle = withPermission(
  (props: any) => (
    <li>
      <Link
        onClick={() => props.showConfirmationAlert(props.record)}
        className="dropdown-item rounded-1"
        to="#"
      >
        <i className="ti ti-trash-x me-2" />
        Supprimer
      </Link>
    </li>
  ),
  [buildPermission(Resource.ACADEMIC_YEAR, Action.DELETE)],
  "masquer",
);

const CycleList = () => {
  //Initialisation des state utilisés
  const [allCycle, setAllCycle] = useState([]);
  const [editCycle, setEditCycle] = useState("");
  const [isLoading, setIsLoading] = useState(true); // Pour le vrai chargement
  const [hasLoaded, setHasLoaded] = useState(false); // Pour éviter "Chargement..." quand il n'y a juste pas de données

  const [refreshKey, setRefreshKey] = useState(0);

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  //Initialisation de sweetAlert
  const MySwal = withReactContent(Swal);

  const getCycleTypeLevelLabels = (record: CycleTable) => {
    const cycleTypeLevels =
      record.CycleTypeLevel || record.CycleTypeLevels || [];
    return cycleTypeLevels
      .map((item) => item.TypeLevel?.label)
      .filter(Boolean)
      .join(", ");
  };

  // Les colonnes de notre tableau
  const columns = [
    {
      title: "Libellé",
      dataIndex: "label",
      render: (text: string, record: any) => `Cycle ${record.label}`,
      sorter: (a: CycleTable, b: CycleTable) => a.label.length - b.label.length,
    },
    {
      title: "Diplôme",
      dataIndex: "diplome",
      render: (text: string, record: any) =>
        `${record.diploma_name} (${record.diploma_abbreviation})`,
      sorter: (a: CycleTable, b: CycleTable) =>
        a.diplome.length - b.diplome.length,
    },
    {
      title: "Types de niveau",
      dataIndex: "CycleTypeLevel",
      render: (_: unknown, record: CycleTable) =>
        getCycleTypeLevelLabels(record) || "-",
      sorter: (a: CycleTable, b: CycleTable) =>
        getCycleTypeLevelLabels(a).length - getCycleTypeLevelLabels(b).length,
    },
    {
      title: "Durée du cycle",
      dataIndex: "duration",
      render: (text: string, record: any) => `${record.duration} ans`,
      sorter: (a: CycleTable, b: CycleTable) => a.label.length - b.label.length,
    },
    {
      title: "Date de création",
      dataIndex: "createdAt",
      render: (date: string) => {
        const d = new Date(date);
        return d.toLocaleDateString("fr-FR", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
      sorter: (a: CycleTable, b: CycleTable) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: any) => (
        <>
          <div className="d-flex align-items-center">
            <div className="dropdown">
              <Link
                to="#"
                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical fs-14" />
              </Link>
              <ul className="dropdown-menu dropdown-menu-right p-3">
                <ProtectedBtnUpdateCycle
                  setEditCycle={setEditCycle}
                  record={record}
                  setShowEdit={setShowEdit}
                />

                <ProtectedBtnDeleteCycle
                  showConfirmationAlert={showConfirmationAlert}
                  record={record}
                />
              </ul>
            </div>
          </div>
        </>
      ),
    },
  ];

  //Traitement en alert de la demande de suppression d'une année académique
  const showConfirmationAlert = (cycle: any) => {
    MySwal.fire({
      title: "Supprimer un cycle ?",
      text: `Souhaitez-vous vraiment supprimer le cycle ${cycle.label} ?`,
      showCancelButton: true,
      // confirmButtonColor: "#00ff00",
      confirmButtonText: "Supprimer !",
      // cancelButtonColor: "#ff0000",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // ── Affiche le spinner pendant la requête ──
        MySwal.fire({
          title: "Suppression en cours...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => MySwal.showLoading(),
        });
        try {
          //On lance la requête de suppression
          await deleteCycle(cycle.id);
          MySwal.fire({
            title: "Cycle supprimé avec succès !",
            icon: "success",
            confirmButtonText: "OK",
          });
          // Relancer le useEffect
          reloadData();
        } catch (error) {
          const err = error as AxiosError<any>;
          console.log(err);

          //On affiche une alerte en cas d'erreur côté serveur
          MySwal.fire({
            title: "Erreur",
            text:
              err.response?.data?.message ||
              err.response?.data?.error ||
              "Une erreur est survenue, réessayez plus tard",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  //Reload de la liste
  const reloadData = () => setRefreshKey((prev) => prev + 1);

  //On recupère la liste des Years en appelant sa fonction
  useEffect(() => {
    setHasLoaded(false);
    setIsLoading(true);

    getAllCycle()
      .then((cycles) => {
        setAllCycle(cycles.data);
        // console.log('Cycle', cycles.data)
      })
      .finally(() => {
        setIsLoading(false); //Les données ne sont plus en chargement
        setHasLoaded(true); //Les données ont été chargé avec succès
      });
  }, [refreshKey]);

  return (
    <>
      {/* Page Header */}
      <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
        {/* Boutton en entête de la page */}
        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
          {/* <TooltipOption/> */}
          <ProtectedBtnAddCycle setShowCreate={setShowCreate} />
        </div>
      </div>
      {/* /Page Header */}

      {/* Tableau liste du personnel administratif */}
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
          <h4 className="mb-3">Liste des Cycles</h4>
        </div>
        <div className="card-body p-0 py-3">
          {/* Staffs List (On affiche le tableau qui si les données son chargées même vide) */}
          {isLoading && !hasLoaded ? (
            <div className="text-center">
              <div className="spinner-border" role="status"></div>
              <span className="visually">Chargement des Données...</span>
            </div>
          ) : (
            <Table columns={columns} dataSource={allCycle} Selection={true} />
          )}
          {/* /Staffs List */}
        </div>
      </div>
      {/* /Page Header */}

      <EditCycleModal
        show={showEdit}
        cycleID={editCycle}
        onClose={() => setShowEdit(false)}
        onSaved={reloadData}
      />
      <CreateCycleModal
        show={showCreate}
        onClose={() => setShowCreate(false)}
        onSaved={reloadData}
      />
    </>
  );
};

// export default withPermission(AcademicYearList, "admin:access", "accès-refuser");
export default CycleList;
